import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Radio, Send, Pencil, Check, X, RotateCcw } from "lucide-react";

const PROVIDERS = [["", "Entegrasyon yok"], ["trendyol", "Trendyol"], ["ticimax", "Ticimax (Site)"], ["n11", "N11"], ["hepsiburada", "Hepsiburada"], ["yemeksepeti", "Yemeksepeti"], ["generic", "Diğer Pazaryeri"]];
const provLabel = (p) => (PROVIDERS.find(([v]) => v === (p || ""))?.[1]) || p;

export function ChannelsManager() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [provider, setProviderState] = useState("");
  const [saving, setSaving] = useState(false);
  const [pushing, setPushing] = useState(null);
  const [editCode, setEditCode] = useState(null);
  const [editName, setEditName] = useState("");
  const [editDesc, setEditDesc] = useState("");

  const load = () => {
    setLoading(true);
    api.get("/sales-channels?status=all").then((r) => setList(r.data || [])).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!code.trim() || !name.trim()) return toast.error("Kanal kodu ve adı zorunlu");
    setSaving(true);
    try {
      await api.post("/sales-channels", { code: code.trim().toUpperCase(), name: name.trim(), description: desc.trim(), provider });
      toast.success("Kanal eklendi");
      setCode(""); setName(""); setDesc(""); setProviderState(""); load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const toggle = async (c, v) => {
    try { await api.put(`/sales-channels/${c.code}`, { is_active: v }); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const setProvider = async (c, p) => {
    try { await api.put(`/sales-channels/${c.code}`, { provider: p }); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const del = async (c) => {
    const msg = c.builtin
      ? `"${c.name}" yerleşik kanalını kaldırmak istediğinize emin misiniz? (Üründe kullanılmıyorsa listelerden kaldırılır)`
      : `"${c.name}" kanalını silmek istediğinize emin misiniz?`;
    if (!window.confirm(msg)) return;
    try { await api.delete(`/sales-channels/${c.code}`); toast.success(c.builtin ? "Kanal kaldırıldı" : "Kanal silindi"); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const startEdit = (c) => { setEditCode(c.code); setEditName(c.name || ""); setEditDesc(c.description || ""); };
  const saveEdit = async () => {
    if (!editName.trim()) return toast.error("Kanal adı zorunlu");
    try { await api.put(`/sales-channels/${editCode}`, { name: editName.trim(), description: editDesc.trim() }); toast.success("Kanal güncellendi"); setEditCode(null); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const restore = async (c) => {
    try { await api.put(`/sales-channels/${c.code}`, { removed: false }); toast.success("Kanal geri getirildi"); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const push = async (c) => {
    setPushing(c.code);
    try {
      const { data } = await api.post(`/sales-channels/${c.code}/push`);
      if (data.simulated) toast.info(data.message || `${data.pushed} ürün hazırlandı (simülasyon)`, { duration: 6000 });
      else toast.success(`${data.pushed} ürün ${provLabel(data.provider)} kanalına gönderildi`);
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setPushing(null); }
  };

  return (
    <div className="max-h-[70vh] space-y-3 overflow-y-auto pr-1" data-testid="sales-channels-dialog">
      <p className="text-[11px] text-slate-400">Perakende, toptan, e-ticaret ve pazaryeri kanallarını yönetin. Bir kanala entegrasyon atayıp "Gönder" ile o kanaldaki aktif ürünleri pazaryerine tek tıkla iletebilirsiniz.</p>
          <div className="rounded-md bg-slate-50 p-3">
            <p className="overline mb-2">Yeni Kanal Ekle</p>
            <div className="grid grid-cols-3 gap-2">
              <Input data-testid="sc-code" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="Kod (N11)" className="font-mono" />
              <Input data-testid="sc-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Ad (N11 Pazaryeri)" className="col-span-2" />
            </div>
            <div className="mt-2 grid grid-cols-3 gap-2">
              <select data-testid="sc-provider" value={provider} onChange={(e) => setProviderState(e.target.value)}
                className="h-10 rounded-md border border-input bg-white px-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-green">
                {PROVIDERS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
              <Input data-testid="sc-desc" value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="Açıklama (opsiyonel)" className="col-span-2" />
            </div>
            <div className="mt-2 flex justify-end">
              <Button data-testid="sc-add" onClick={add} disabled={saving} className="bg-brand-green text-white hover:bg-brand-green-600">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="mr-1 h-4 w-4" /> Ekle</>}
              </Button>
            </div>
          </div>
          {loading ? (
            <div className="py-6 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin text-slate-400" /></div>
          ) : (
            <div className="space-y-1.5">
              {list.map((c) => (
                <div key={c.code} data-testid={`sc-row-${c.code}`} className={`rounded-md px-3 py-2 ring-1 ring-inset ${c.removed ? "bg-slate-50 opacity-80 ring-slate-200" : "bg-white ring-slate-200"}`}>
                  {c.removed ? (
                    <div className="flex items-center gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="truncate text-sm font-semibold text-slate-500 line-through">{c.name}</p>
                        <p className="font-mono text-[10px] text-slate-400">{c.code} · KALDIRILDI</p>
                      </div>
                      <Button data-testid={`sc-restore-${c.code}`} size="sm" variant="outline" onClick={() => restore(c)} className="h-7 text-[11px]">
                        <RotateCcw className="mr-1 h-3 w-3 text-brand-green" /> Geri Getir
                      </Button>
                    </div>
                  ) : (
                  <>
                  <div className="flex items-center gap-2">
                    {editCode === c.code ? (
                      <div className="flex-1 min-w-0 space-y-1">
                        <Input data-testid={`sc-edit-name-${c.code}`} value={editName} onChange={(e) => setEditName(e.target.value)} placeholder="Kanal adı" className="h-8" />
                        <Input data-testid={`sc-edit-desc-${c.code}`} value={editDesc} onChange={(e) => setEditDesc(e.target.value)} placeholder="Açıklama (opsiyonel)" className="h-7 text-xs" />
                      </div>
                    ) : (
                      <div className="flex-1 min-w-0">
                        <p className="truncate text-sm font-semibold text-slate-800">
                          {c.name} {c.builtin && <span className="ml-1 rounded bg-slate-100 px-1 text-[9px] text-slate-500">YERLEŞİK</span>}
                        </p>
                        <p className="font-mono text-[10px] text-slate-400">{c.code}{c.description ? ` · ${c.description}` : ""}</p>
                      </div>
                    )}
                    {editCode === c.code ? (
                      <>
                        <button data-testid={`sc-edit-save-${c.code}`} onClick={saveEdit} className="text-brand-green hover:text-brand-green-600" title="Kaydet"><Check className="h-4 w-4" /></button>
                        <button data-testid={`sc-edit-cancel-${c.code}`} onClick={() => setEditCode(null)} className="text-slate-400 hover:text-slate-600" title="Vazgeç"><X className="h-4 w-4" /></button>
                      </>
                    ) : (
                      <>
                        <Switch data-testid={`sc-active-${c.code}`} checked={c.is_active !== false} onCheckedChange={(v) => toggle(c, v)} />
                        <button data-testid={`sc-edit-${c.code}`} onClick={() => startEdit(c)} className="text-slate-500 hover:text-brand-green" title="Adı düzenle"><Pencil className="h-4 w-4" /></button>
                        <button data-testid={`sc-del-${c.code}`} onClick={() => del(c)} className="text-red-500 hover:text-red-700" title={c.builtin ? "Kaldır" : "Sil"}><Trash2 className="h-4 w-4" /></button>
                      </>
                    )}
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-2 border-t border-slate-100 pt-2">
                    <span className="text-[10px] text-slate-400">Entegrasyon:</span>
                    <select data-testid={`sc-provider-${c.code}`} value={c.provider || ""} onChange={(e) => setProvider(c, e.target.value)}
                      className="h-7 rounded-md border border-input bg-white px-1.5 text-[11px] focus:outline-none focus:ring-1 focus:ring-brand-green">
                      {PROVIDERS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                    {c.last_push_at && (
                      <span className="text-[10px] text-slate-400">
                        Son gönderim: {new Date(c.last_push_at).toLocaleString("tr-TR")} · {c.last_push_count || 0} ürün{c.last_push_simulated ? " (simülasyon)" : ""}
                      </span>
                    )}
                    <Button data-testid={`sc-push-${c.code}`} size="sm" variant="outline" onClick={() => push(c)} disabled={pushing === c.code}
                      className="ml-auto h-7 text-[11px]">
                      {pushing === c.code ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <Send className="mr-1 h-3 w-3 text-brand-green" />} Gönder
                    </Button>
                  </div>
                  </>
                  )}
                </div>
              ))}
              {list.length === 0 && <p className="py-4 text-center text-xs text-slate-400">Kanal yok.</p>}
            </div>
          )}
    </div>
  );
}

export default function SalesChannelsDialog({ open, onClose }) {
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading"><Radio className="h-5 w-5 text-brand-green" /> Satış Kanalları</DialogTitle>
          <DialogDescription>Perakende, toptan, e-ticaret ve pazaryeri kanallarını yönetin.</DialogDescription>
        </DialogHeader>
        <ChannelsManager />
      </DialogContent>
    </Dialog>
  );
}
